import React, { useState } from "react";
import { X, User, Mail, Phone, KeyRound, Save, CheckCircle2 } from "lucide-react";
import { api } from "../api/client";

export default function MyAccountModal({ user, onClose }) {
  const [tab, setTab] = useState("profile"); // profile | password
  const [name, setName] = useState(user?.name || "");
  const [phone, setPhone] = useState(user?.phone || "");
  const [currentPass, setCurrentPass] = useState("");
  const [newPass, setNewPass] = useState("");
  const [confirmPass, setConfirmPass] = useState("");
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");
  const [saving, setSaving] = useState(false);

  const saveProfile = async () => {
    setErr(""); setMsg(""); setSaving(true);
    try {
      await api.patch("/auth/me", { name, phone });
      setMsg("Perfil actualizado ✓");
    } catch (e) {
      setErr(e.response?.data?.detail || "Error al guardar");
    } finally { setSaving(false); }
  };

  const changePassword = async () => {
    setErr(""); setMsg("");
    if (newPass.length < 8) return setErr("La nueva contraseña debe tener mínimo 8 caracteres");
    if (newPass !== confirmPass) return setErr("Las contraseñas no coinciden");
    setSaving(true);
    try {
      await api.post("/auth/change-password", { current_password: currentPass, new_password: newPass });
      setMsg("Contraseña actualizada ✓");
      setCurrentPass(""); setNewPass(""); setConfirmPass("");
    } catch (e) {
      setErr(e.response?.data?.detail || "Error al cambiar contraseña");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid="myaccount-modal">
      <div className="bg-white rounded-xl shadow-2xl max-w-md w-full relative">
        <button onClick={onClose} className="absolute top-3 right-3 p-1 rounded hover:bg-neutral-100 z-10" data-testid="myaccount-close">
          <X className="w-5 h-5 text-neutral-500" />
        </button>

        <div className="px-6 pt-6 pb-4 border-b border-neutral-100">
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 rounded-full bg-[#1A237E] text-[#C4FF00] flex items-center justify-center font-bold text-xl">
              {(user?.name || "??").split(" ").slice(0, 2).map(n => n[0]).join("").toUpperCase()}
            </div>
            <div>
              <div className="font-display text-lg font-bold text-neutral-900">{user?.name}</div>
              <div className="text-sm text-neutral-500">{user?.email}</div>
              <span className="chip chip-indigo mt-1 inline-block">{user?.role}</span>
            </div>
          </div>
        </div>

        <div className="flex border-b border-neutral-100">
          <button
            onClick={() => { setTab("profile"); setMsg(""); setErr(""); }}
            className={`flex-1 px-4 py-3 text-sm font-medium border-b-2 transition ${tab === "profile" ? "border-[#3949AB] text-[#3949AB]" : "border-transparent text-neutral-500"}`}
            data-testid="tab-profile"
          >
            <User className="w-4 h-4 inline mr-1.5" /> Perfil
          </button>
          <button
            onClick={() => { setTab("password"); setMsg(""); setErr(""); }}
            className={`flex-1 px-4 py-3 text-sm font-medium border-b-2 transition ${tab === "password" ? "border-[#3949AB] text-[#3949AB]" : "border-transparent text-neutral-500"}`}
            data-testid="tab-password"
          >
            <KeyRound className="w-4 h-4 inline mr-1.5" /> Contraseña
          </button>
        </div>

        <div className="px-6 py-5 space-y-4">
          {tab === "profile" ? (
            <>
              <div>
                <label className="text-[11px] font-semibold uppercase tracking-wider text-neutral-500">Nombre completo</label>
                <input value={name} onChange={e => setName(e.target.value)} className="w-full mt-1.5 px-3 py-2.5 border border-neutral-300 rounded text-sm focus:border-[#3949AB] outline-none" data-testid="profile-name" />
              </div>
              <div>
                <label className="text-[11px] font-semibold uppercase tracking-wider text-neutral-500">Correo</label>
                <input value={user?.email} disabled className="w-full mt-1.5 px-3 py-2.5 border border-neutral-200 bg-neutral-50 rounded text-sm text-neutral-500" />
                <p className="text-[10px] text-neutral-400 mt-1">El correo no se puede cambiar (contacta al administrador)</p>
              </div>
              <div>
                <label className="text-[11px] font-semibold uppercase tracking-wider text-neutral-500">Teléfono</label>
                <input value={phone} onChange={e => setPhone(e.target.value)} placeholder="+51 9XX XXX XXX" className="w-full mt-1.5 px-3 py-2.5 border border-neutral-300 rounded text-sm focus:border-[#3949AB] outline-none" data-testid="profile-phone" />
              </div>
              {err && <div className="chip chip-danger">{err}</div>}
              {msg && <div className="p-2.5 bg-emerald-50 border border-emerald-200 rounded text-sm text-emerald-700 flex items-center gap-2"><CheckCircle2 className="w-4 h-4" />{msg}</div>}
              <button onClick={saveProfile} disabled={saving} className="w-full btn-primary !py-2.5 justify-center" data-testid="save-profile">
                <Save className="w-4 h-4" /> {saving ? "Guardando..." : "Guardar cambios"}
              </button>
            </>
          ) : (
            <>
              <div>
                <label className="text-[11px] font-semibold uppercase tracking-wider text-neutral-500">Contraseña actual</label>
                <input type="password" value={currentPass} onChange={e => setCurrentPass(e.target.value)} className="w-full mt-1.5 px-3 py-2.5 border border-neutral-300 rounded text-sm focus:border-[#3949AB] outline-none" data-testid="current-password" />
              </div>
              <div>
                <label className="text-[11px] font-semibold uppercase tracking-wider text-neutral-500">Nueva contraseña</label>
                <input type="password" value={newPass} onChange={e => setNewPass(e.target.value)} minLength={8} className="w-full mt-1.5 px-3 py-2.5 border border-neutral-300 rounded text-sm focus:border-[#3949AB] outline-none" data-testid="new-password" />
              </div>
              <div>
                <label className="text-[11px] font-semibold uppercase tracking-wider text-neutral-500">Confirmar nueva</label>
                <input type="password" value={confirmPass} onChange={e => setConfirmPass(e.target.value)} className="w-full mt-1.5 px-3 py-2.5 border border-neutral-300 rounded text-sm focus:border-[#3949AB] outline-none" data-testid="confirm-password" />
              </div>
              {err && <div className="chip chip-danger">{err}</div>}
              {msg && <div className="p-2.5 bg-emerald-50 border border-emerald-200 rounded text-sm text-emerald-700 flex items-center gap-2"><CheckCircle2 className="w-4 h-4" />{msg}</div>}
              <button onClick={changePassword} disabled={saving} className="w-full btn-primary !py-2.5 justify-center" data-testid="save-password">
                <KeyRound className="w-4 h-4" /> {saving ? "Cambiando..." : "Cambiar contraseña"}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
